import { Router } from '@angular/router';
import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { of, ReplaySubject } from 'rxjs';
import { debounceTime, skip, switchMap, takeUntil } from 'rxjs/operators';
import { flatten, omit } from 'lodash-es';

import { DESC_ENTITY } from '@constants/desc-entity';
import { FormHelper } from '@helpers/form.helper';
import { INPUT_TYPES_MAP } from '@constants/input-types-map';
import { IsfV1EstimatedInfoModel } from '@data-layer/models/isf-v1';
import { WEIGHT_UNIT_LIST } from '@constants/weight-unit-list';
import { enumWeightUnit } from '@data-layer/enums/common';
import { DEFAULT_CURRENCY_CODE } from '@constants/currency';
import {
  CHINA_COUNTRY_CODE,
  DEFAULT_ISF_UNIT_FOR_MULTIPLE_ISF,
  ENTERED_VALUE_CHINA_LIMIT,
  ENTERED_VALUE_TOTAL_LIMIT,
} from '@constants/constants';
import { IsfStateService } from '../../../services';
import { InvoiceModel } from '@data-layer/models/entry-summaries';
import { IsfEstimatedInfoModel } from '@data-layer/models/isf';

@Component({
  selector: 'desktop-shipment-details-informal',
  template: `
    <form [formGroup]="formGroup" class="container pr-0 pb-0 pl-0" autocomplete="off">
      <div class="form-row">
        <div class="col-12">
          <app-control-wrapper [control]="formGroup.get('estimatedValue')" [errors]="errors.estimatedValue">
            <label for="estimatedValue" control-label>{{ 'FORM_LABEL.ENTERED_VALUE' | translate }}</label>
            <div control-wrapper class="d-flex">
              <input
                type="text"
                [formControl]="formGroup.get('estimatedValue')"
                class="form-control estimated-value"
                id="estimatedValue"
                name="estimatedValue"
                placeholder="{{ 'FORM_LABEL.ENTERED_VALUE' | translate }}"
                [mask]="decimalInputMap.mask"
                [pattern]="decimalInputMap.pattern"
                [dropSpecialCharacters]="false"
                [appMaskErrorTooltip]="decimalInputMap.maskErrorTooltip"
                inputmode="decimal"
              />
              <div class="form-control currency">{{ DEFAULT_CURRENCY_CODE }}</div>
            </div>
          </app-control-wrapper>
        </div>
        <div class="col-12">
          <div class="form-row mb-3" [ngClass]="{ 'multiple-hts-hint-row': isMultipleHts }">
            <div class="col-4">
              <app-control-wrapper [control]="formGroup.get('quantity')" [errors]="errors.default">
                <label for="quantity" control-label>{{ 'LABELS.QUANTITY' | translate }}</label>
                <input
                  type="text"
                  [formControl]="formGroup.get('quantity')"
                  class="form-control"
                  id="quantity"
                  name="quantity"
                  placeholder="{{ 'LABELS.QUANTITY' | translate }}"
                  [mask]="decimalInputMap.mask"
                  [pattern]="decimalInputMap.pattern"
                  [dropSpecialCharacters]="false"
                  [appMaskErrorTooltip]="decimalInputMap.maskErrorTooltip"
                  inputmode="decimal"
                  control-wrapper
                />
              </app-control-wrapper>
            </div>
            <div class="col-4">
              <auto-select
                [label]="'FORM_LABEL.UNIT_OF_MEASURE'"
                [bindLabel]="['code', 'description']"
                [bindOption]="['code', 'description']"
                [bindValue]="'code'"
                [fc]="formGroup.get('quantityUnit')"
                [change]="getQuantityUnits()"
                [errors]="errors.default"
              ></auto-select>
            </div>
            <div class="col-4">
              <app-control-wrapper [control]="formGroup.get('weight')" [errors]="errors.default">
                <label for="weight" control-label>{{ 'LABELS.WEIGHT' | translate }}</label>
                <div control-wrapper class="d-flex position-relative">
                  <input
                    type="text"
                    [formControl]="formGroup.get('weight')"
                    class="form-control weight-type-input"
                    id="weight"
                    name="weight"
                    placeholder="{{ 'LABELS.WEIGHT' | translate }}"
                    [mask]="decimalInputMap.mask"
                    [pattern]="decimalInputMap.pattern"
                    [dropSpecialCharacters]="false"
                    [appMaskErrorTooltip]="decimalInputMap.maskErrorTooltip"
                    inputmode="decimal"
                    control-wrapper
                  />

                  <ng-select
                    control-wrapper
                    [items]="weightUnitList"
                    bindValue="value"
                    bindLabel="label"
                    formControlName="weightUnit"
                    id="weightUnit"
                    name="weightUnit"
                    [clearable]="false"
                    class="custom weight-type"
                  ></ng-select>
                </div>
              </app-control-wrapper>
            </div>
            <div *ngIf="isMultipleHts" class="col-12 multiple-hts-hint">
              {{ 'FORM_HELPER.UNIT_OF_MEASURE_MULTIPLE_HTS' | translate }}
            </div>
          </div>
        </div>
      </div>
    </form>
  `,
  styleUrls: ['./desktop-shipment-details-informal.component.scss'],
})
export class DesktopShipmentDetailsInformalComponent implements OnInit, OnChanges, OnDestroy {
  @Input() public isSideBar: boolean;
  @Input() public isUpdateIsfOnChange: boolean = true;
  @Input() public invoices: InvoiceModel[] = [];
  @Input() public formGroupControlsOnUpdate: 'change' | 'blur' | 'submit' = 'blur';
  @Output() public formUpdated: EventEmitter<IsfEstimatedInfoModel> = new EventEmitter<IsfEstimatedInfoModel>();
  @Output() public formValidityUpdated: EventEmitter<boolean> = new EventEmitter<boolean>();

  public formGroup: UntypedFormGroup;
  public errors = this.createErrors();
  public estimatedInfo: IsfV1EstimatedInfoModel;
  public info = DESC_ENTITY.IE;
  public decimalInputMap = INPUT_TYPES_MAP.DECIMAL;
  public weightUnitList = WEIGHT_UNIT_LIST;
  public DEFAULT_CURRENCY_CODE = DEFAULT_CURRENCY_CODE;
  public isModalOpen = false;
  public isMultipleHts: boolean = false;
  public isChina: boolean = false;
  public enteredValueLimit: number = ENTERED_VALUE_TOTAL_LIMIT;

  private destroy$: ReplaySubject<boolean> = new ReplaySubject<boolean>();

  constructor(private isfStateService: IsfStateService, private fb: UntypedFormBuilder, private router: Router, private fh: FormHelper) {}

  public ngOnInit(): void {
    this.initEnteredValueLimit();
    this.errors = this.createErrors();

    this.formGroup = this.fb.group(this.createFormModel(this.formGroupControlsOnUpdate));

    if (!this.isSideBar) {
      this.formGroup.valueChanges
        .pipe(
          takeUntil(this.destroy$),
          skip(1),
          switchMap((val) => {
            if (this.isUpdateIsfOnChange) {
              return this.isfStateService.updateIsf(
                {
                  ...this.isfStateService.currentIsf,
                  importerId: this.isfStateService.currentIsf?.importer?.id,
                  estimatedInformation: { ...this.isfStateService.currentIsf.estimatedInformation, ...this.formGroup.value },
                },
                false,
              );
            } else {
              return of(this.isfStateService.currentIsf);
            }
          }),
        )
        .subscribe((isf) => {
          if (this.isUpdateIsfOnChange) {
            this.isfStateService.currentIsf = isf;
          }
          this.formUpdated.emit(this.formGroup.value);
        });
    } else {
      this.formGroup.valueChanges.pipe(takeUntil(this.destroy$), skip(1)).subscribe(() => {
        this.formUpdated.emit(this.formGroup.value);
      });
    }

    if (!this.isfStateService.currentIsf) {
      this.router.navigate(['dashboard']);
      return;
    }

    this.formGroup.valueChanges
      .pipe(debounceTime(100), takeUntil(this.destroy$))
      .subscribe(() => this.formValidityUpdated.emit(this.formGroup.valid));

    this.patchData();

    this.checkMultipleHts();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (!!changes?.invoices && !changes?.invoices?.firstChange) {
      this.initEnteredValueLimit();
      this.errors = this.createErrors();
      this.formGroup.get('estimatedValue').setValidators([Validators.required, Validators.max(this.enteredValueLimit)]);
    }
  }

  public ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  public getQuantityUnits() {
    return this.isfStateService.getQuantityUnits.bind(this.isfStateService);
  }

  public checkValidState(): void {}

  private patchData() {
    const estimatedInfo = this.isfStateService.getEstimatedInformation();
    this.estimatedInfo = !!estimatedInfo ? (omit(estimatedInfo, ['validationMessages']) as IsfV1EstimatedInfoModel) : null;
    const weightUnit = this.estimatedInfo?.weightUnit;

    if (!!this.estimatedInfo) {
      this.formGroup.patchValue({
        ...this.estimatedInfo,
        weightUnit: weightUnit || enumWeightUnit.KG,
      });
      return;
    }
  }

  private createErrors() {
    return {
      estimatedValue: {
        max: 'BACK_ERROR.INFORMAL_SHIPMENT_TYPE_ENTERED_VALUE_TOO_BIG_DESKTOP',
        required: 'FORM_ERROR.REQUIRED',
      },
      default: {
        required: 'FORM_ERROR.REQUIRED',
      },
    };
  }

  private createFormModel(updateOn: 'change' | 'blur' | 'submit') {
    return {
      estimatedValue: new UntypedFormControl(null, {
        validators: [Validators.required, Validators.max(this.enteredValueLimit)],
        updateOn,
      }),
      quantity: new UntypedFormControl(null, { validators: [Validators.required], updateOn }),
      quantityUnit: new UntypedFormControl(null, { validators: [Validators.required], updateOn }),
      weight: new UntypedFormControl(null, { validators: [Validators.required], updateOn }),
      weightUnit: new UntypedFormControl(enumWeightUnit.KG, { validators: [Validators.required], updateOn }),
    };
  }

  private checkMultipleHts() {
    this.isMultipleHts = !!(this.invoices || []).find((invoice) => invoice?.lines?.length > 1);
    if (this.isMultipleHts && !this.formGroup.get('quantityUnit').value) {
      this.formGroup.get('quantityUnit').patchValue(DEFAULT_ISF_UNIT_FOR_MULTIPLE_ISF);
    }
  }

  private initEnteredValueLimit() {
    const lines = flatten((this.invoices || []).map((invoice) => invoice?.lines));
    this.isChina = lines.some((country) => country.countryOfOrigin?.code === CHINA_COUNTRY_CODE);
    this.enteredValueLimit = this.isChina ? ENTERED_VALUE_CHINA_LIMIT : ENTERED_VALUE_TOTAL_LIMIT;
  }
}
